import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { User, LoginRequest, AuthResponse } from '../types'
import * as authApi from '../services/auth'

interface AuthState {
  // State
  user: User | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  _hasHydrated: boolean // Track if state has been rehydrated from storage

  // Actions
  login: (credentials: LoginRequest) => Promise<AuthResponse>
  logout: () => void
  verifyToken: () => Promise<void>
  clearError: () => void
  setLoading: (loading: boolean) => void
  _setHasHydrated: (hydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        loading: false,
        error: null,
        isAuthenticated: false,
        _hasHydrated: false,

        login: async (credentials: LoginRequest) => {
          try {
            set({ loading: true, error: null })
            const response: AuthResponse = await authApi.login(credentials)

            // If 2FA is required, don't set user/token in state
            if (response.requires2FA) {
              set({ loading: false })
              return response
            }

            set({
              user: response.user,
              isAuthenticated: true,
              loading: false,
            })

            // Store token in localStorage
            localStorage.setItem('token', response.token)
            return response
          } catch (error: any) {
            console.error('Login error:', error)
            set({
              error:
                error.response.data.error ||
                error.response.data ||
                'Login failed',
              loading: false,
            })
            throw error
          }
        },

        logout: () => {
          set({
            user: null,
            isAuthenticated: false,
            loading: false,
            error: null,
          })
          localStorage.removeItem('token')
        },

        verifyToken: async () => {
          const token = localStorage.getItem('token')

          if (!token) {
            // No token, ensure we're logged out
            if (get().isAuthenticated) {
              set({
                loading: false,
                isAuthenticated: false,
                user: null,
              })
            }
            return
          }

          // If we have persisted auth state, don't show loading
          const hasPersistedAuth = get().isAuthenticated && get().user
          if (!hasPersistedAuth) {
            set({ loading: true })
          }

          try {
            const userData = await authApi.verifyToken(token)

            set({
              user: userData,
              isAuthenticated: true,
              loading: false,
            })
          } catch (error: any) {
            console.error('Token verification failed:', error)
            // Only clear auth if we don't have persisted state or token is truly invalid
            // This prevents logout on network errors when we have valid persisted state
            const isUnauthorized = error?.response?.status === 401

            if (isUnauthorized) {
              localStorage.removeItem('token')
              set({
                user: null,
                isAuthenticated: false,
                loading: false,
                error: null,
              })
            } else {
              // Network error or other issue - keep persisted state if we have it
              set({ loading: false })
            }
          }
        },

        clearError: () => {
          set({ error: null })
        },

        setLoading: (loading: boolean) => {
          set({ loading })
        },

        _setHasHydrated: (hydrated: boolean) => {
          set({ _hasHydrated: hydrated })
        },
      }),
      {
        name: 'auth-store',
        // Only persist user data and isAuthenticated status
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state, error) => {
          // Mark as hydrated after rehydration completes (even if no stored data exists)
          if (!error && state) {
            state._setHasHydrated(true)
          }
        },
      },
    ),
    {
      name: 'auth-store',
    },
  ),
)
